import Footer1 from "../Layout/Footer/Footer1";
import NavbarTransparent from "../Layout/Navbar2/NavbarTransparent";

import { useDispatch, useSelector } from "react-redux";
import { Dispatch, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Actions from "../store/actions";
import Banner from "../FlightFinalOffer/Banner";
import { PAGE_BG, globalKeyframes } from "../FlightFinalOffer/ui";
import Page3 from "./Page3";
import Page4 from "./Page4";
import { PayLaterMessage, PayLaterSkeleton } from "./states";

import "../FlightFinalOffer/scrollbar.css";

// Pay-later checkout for a fare booked on hold, opened from the emailed
// link /payment/:feature/:ref. Styled after the flight checkout (/final).
//
// Only transactions that actually went through hold() are payable here —
// vigo's hold_details refuses anything else (not_a_held_booking), and the
// page also checks bookedOnHold itself.
type Phase =
  | "loading"
  | "error"
  | "pay"
  | "confirming"
  | "success"
  | "booking_failed"
  | "timeout";

const ERROR_COPY: Record<string, { title: string; body: string }> = {
  not_a_held_booking: {
    title: "This booking wasn't placed on hold",
    body: "This payment link only works for fares booked on hold, so there's nothing to pay here. If you already started a payment, check your email for a confirmation, or search for your flight again.",
  },
  hold_failed: {
    title: "We couldn't hold this fare",
    body: "The airline didn't accept the hold, so this fare isn't reserved and can't be paid for. Please search for your flight again.",
  },
  hold_expired: {
    title: "This hold has expired",
    body: "The airline only keeps held fares for a limited time, and this one has run out. Please search for your flight again to book it.",
  },
  transaction_not_found: {
    title: "Payment link not found",
    body: "We couldn't find a booking for this link. Check that you opened the full link from your email.",
  },
};

const GENERIC_ERROR = {
  title: "Something went wrong",
  body: "We couldn't load this booking. Please try again in a moment, or contact support.",
};

const CONFIRM_TIMEOUT_MS = 2 * 60 * 1000;

const holdExpiry = (trans: any): Date | null => {
  const raw = trans?.metadata?.flightHold?.holdlimittime;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const Payment = () => {
  const navigate = useNavigate();
  const { feature, ref } = useParams();
  const dispatch: Dispatch<any> = useDispatch();
  const trans = useSelector((state: any) => state.hold.trans);
  const [phase, setPhase] = useState<Phase>("loading");
  const [errorCode, setErrorCode] = useState<string>("");
  const settledRef = useRef(false);

  const fail = (code: string) => {
    setErrorCode(code);
    setPhase("error");
  };

  useEffect(() => {
    dispatch(Actions.setIsActive());
  }, []);

  useEffect(() => {
    if (!feature || !ref) {
      fail("transaction_not_found");
      return;
    }

    setPhase("loading");
    dispatch(
      Actions.loadHoldDetails(
        { feature, reference: ref },
        (data: any) => {
          const t = data?.trans;
          if (!t?.bookedOnHold) return fail("not_a_held_booking");

          if (t.bookingStatus === "s") return setPhase("success");
          if (t.status === "c") return setPhase("confirming");
          if (t.status === "h" && t.bookingStatus === "f") return fail("hold_failed");

          const expiry = holdExpiry(t);
          if (expiry && expiry.getTime() < Date.now()) return fail("hold_expired");

          setPhase("pay");
        },
        (err: any) => {
          const message = err?.response?.data?.message;
          fail(typeof message === "string" ? message : "");
        },
      ),
    );
  }, [feature, ref]);

  // After payment: the gateway webhook confirms the charge, then vigo
  // issues the ticket. Poll until bookingStatus settles.
  useEffect(() => {
    if (phase !== "confirming" || !ref) return;
    const startedAt = Date.now();
    settledRef.current = false;

    const settle = (next: Phase) => {
      settledRef.current = true;
      clearInterval(timer);
      setPhase(next);
    };

    const poll = () => {
      dispatch(
        Actions.getTransactionStatus(
          ref,
          (data: any) => {
            if (settledRef.current) return;
            if (data?.bookingStatus === "s") return settle("success");
            if (data?.bookingStatus === "f" && data?.status === "c") return settle("booking_failed");
            if (Date.now() - startedAt >= CONFIRM_TIMEOUT_MS) settle("timeout");
          },
          () => {
            /* transient network hiccup — wait for the next tick */
          },
        ),
      );
    };

    poll();
    const timer = setInterval(poll, 4000);
    return () => clearInterval(timer);
  }, [phase, ref]);

  const content = () => {
    switch (phase) {
      case "loading":
        return <PayLaterSkeleton />;
      case "error": {
        const copy = ERROR_COPY[errorCode] || GENERIC_ERROR;
        return (
          <PayLaterMessage
            tone="error"
            title={copy.title}
            body={copy.body}
            actionLabel="Search flights"
            onAction={() => navigate("/flights")}
          />
        );
      }
      case "pay":
        return <Page3 trans={trans} expiry={holdExpiry(trans)} onPaid={() => setPhase("confirming")} />;
      case "confirming":
        return (
          <PayLaterMessage
            tone="progress"
            title="Confirming your payment"
            body="We're confirming your payment and issuing your ticket with the airline. This usually takes under a minute — please keep this page open."
          />
        );
      case "success":
        return <Page4 trans={trans} />;
      case "booking_failed":
        return (
          <PayLaterMessage
            tone="error"
            title="Payment received, ticket not issued"
            body={`Your payment went through, but the airline didn't issue the ticket. Please contact support with reference ${ref} and we'll sort it out or refund you.`}
            actionLabel="Back to flights"
            onAction={() => navigate("/flights")}
          />
        );
      case "timeout":
        return (
          <PayLaterMessage
            tone="warning"
            title="Still confirming your payment"
            body={`This is taking longer than usual. If you were charged, your ticket will be emailed to you once the airline confirms it. Your reference is ${ref}.`}
            actionLabel="Check again"
            onAction={() => setPhase("confirming")}
          />
        );
    }
  };

  return (
    <div style={{ backgroundColor: PAGE_BG, minHeight: "100vh" }}>
      <style>{globalKeyframes}</style>
      <NavbarTransparent />
      <Banner />
      <main className="max-w-[1180px] mx-auto px-5 md:px-16 py-6 pb-24">{content()}</main>
      <Footer1 />
    </div>
  );
};
export default Payment;
